import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, Validators } from '@angular/forms'
import { PersonaModel } from '@app/v2/personas/models/persona.model'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'

@Component({
  selector: 'create-persona-modal',
  standalone: true,
  imports: [ModalComponent, BasicInputComponent, ProgressCircleComponent],
  templateUrl: './create-persona-modal.component.html',
  styleUrl: './create-persona-modal.component.scss'
})
export class CreatePersonaModalComponent implements OnChanges {
  @Input() id: string = 'create-persona-modal'
  @Input() apiErrorBody: unknown | null = null
  @Input() headerText: string = 'Create Persona'
  @Input() submitButtonText: string = 'Continue'
  @Input() initialName: string = ''
  @Output() onCreate = new EventEmitter<string>()
  @Output() onCancel = new EventEmitter<void>()

  public isSubmitting = input<boolean>(false)
  public personaName = new FormControl('', { nonNullable: true, validators: [Validators.required] })
  public readonly plusIcon = `
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_39982_158062)">
    <path d="M15.1111 7.11111H8.88889V0.888889C8.88889 0.653141 8.79524 0.427048 8.62854 0.260349C8.46184 0.0936505 8.23575 0 8 0C7.76425 0 7.53816 0.0936505 7.37146 0.260349C7.20476 0.427048 7.11111 0.653141 7.11111 0.888889V7.11111H0.888889C0.653141 7.11111 0.427048 7.20476 0.260349 7.37146C0.0936505 7.53816 0 7.76425 0 8C0 8.23575 0.0936505 8.46184 0.260349 8.62854C0.427048 8.79524 0.653141 8.88889 0.888889 8.88889H7.11111V15.1111C7.11111 15.3469 7.20476 15.573 7.37146 15.7397C7.53816 15.9064 7.76425 16 8 16C8.23575 16 8.46184 15.9064 8.62854 15.7397C8.79524 15.573 8.88889 15.3469 8.88889 15.1111V8.88889H15.1111C15.3469 8.88889 15.573 8.79524 15.7397 8.62854C15.9064 8.46184 16 8.23575 16 8C16 7.76425 15.9064 7.53816 15.7397 7.37146C15.573 7.20476 15.3469 7.11111 15.1111 7.11111Z" fill="#111928"/>
    </g>
    <defs>
    <clipPath id="clip0_39982_158062">
    <rect width="16" height="16" fill="white"/>
    </clipPath>
    </defs>
    </svg>
  `

  private readonly _destroyRef = inject(DestroyRef)
  private _serverErrorKeysApplied: string[] = []

  constructor() {
    this.personaName.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._clearAppliedServerErrors()
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialName']) {
      this.personaName.setValue(this.initialName ?? '', { emitEvent: false })
      this._clearAppliedServerErrors()
    }

    if (!changes['apiErrorBody']) return
    const merged = PersonaModel.validationErrorsFromFieldMessages(this.apiErrorBody, 'name')
    if (!merged || !Object.keys(merged).length) {
      this._clearAppliedServerErrors()
      return
    }
    this._serverErrorKeysApplied = Object.keys(merged)
    this.personaName.setErrors({ ...(this.personaName.errors ?? {}), ...merged })
    this.personaName.markAsTouched()
  }

  /**
   * Removes keys last merged from API-derived errors (e.g. after the user edits or the parent clears the input).
   */
  private _clearAppliedServerErrors(): void {
    if (!this._serverErrorKeysApplied.length) return
    const current = this.personaName.errors
    if (!current) {
      this._serverErrorKeysApplied = []
      return
    }
    const rest = { ...current }
    for (const key of this._serverErrorKeysApplied) delete rest[key]
    this._serverErrorKeysApplied = []
    this.personaName.setErrors(Object.keys(rest).length ? rest : null)
    this.personaName.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Submits when the user presses Enter in the name field.
   * @param {KeyboardEvent} event - The keyboard event
   */
  public onNameKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this.onContinue()
  }

  /**
   * Handles the continue button click.
   * @returns {void}
   */
  public onContinue(): void {
    if (this.isSubmitting()) return
    this._clearAppliedServerErrors()
    this.personaName.markAsTouched()
    const name = (this.personaName.value || '').trim()
    if (!name || this.personaName.invalid) return

    this.onCreate.emit(name)
  }

  /**
   * Handles the modal close.
   * @returns {void}
   */
  public handleModalClose(): void {
    if (this.isSubmitting()) return
    this._clearAppliedServerErrors()
    this.personaName.markAsUntouched()
    this.onCancel.emit()
  }
}
